import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosConfig";
import "./ChatComponent.css";
import piqLogo from "../Assets/Images/Dark_Logo_Transparent.png";

// --- Inline SVG Icons --------------------------------------------------------

const Icons = {
  ChevronLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Message: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Send: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  File: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

// --- Helpers -----------------------------------------------------------------

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

const idsEqual = (a, b) => a != null && b != null && String(a) === String(b);

const getClubAcronym = (clubName) => {
  if (!clubName) return "?";
  const words = clubName.trim().split(/\s+/);
  return words.map((w) => w.charAt(0).toUpperCase()).join("").slice(0, 4) || "?";
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + time;
};

const formatConversationTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return "Yesterday";
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatDateSeparator = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
};

const shouldShowDateSeparator = (current, prev) => {
  if (!prev) return true;
  const d1 = new Date(current.timestamp);
  const d2 = new Date(prev.timestamp);
  return d1.toDateString() !== d2.toDateString();
};

const isGroupedMessage = (current, prev) => {
  if (!prev) return false;
  if (current.is_system_message || prev.is_system_message) return false;
  if (current.is_sent_by_user !== prev.is_sent_by_user) return false;
  const diff = new Date(current.timestamp) - new Date(prev.timestamp);
  return diff < 60000; // Within 1 minute
};

// --- Component ---------------------------------------------------------------

const Chat = ({ userId }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Tab state — respect ?tab= query param on mount
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    return ["general", "member-portal", "clubs"].includes(tab) ? tab : "general";
  });

  // General/Member Portal state (existing)
  const [generalChats, setGeneralChats] = useState([]);
  const [memberPortalChats, setMemberPortalChats] = useState([]);
  const [receiverId, setReceiverId] = useState(null);
  const [selectedPlayRequestId, setSelectedPlayRequestId] = useState(null);
  const [receiverUsername, setReceiverUsername] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);

  // Clubs tab state
  const [clubThreads, setClubThreads] = useState([]);
  const [selectedClubThread, setSelectedClubThread] = useState(null);
  const [clubThreadData, setClubThreadData] = useState(null);

  // Denial reason prompt state
  const [showDenialPrompt, setShowDenialPrompt] = useState(false);
  const [denialRequestId, setDenialRequestId] = useState(null);
  const [selectedDenialReason, setSelectedDenialReason] = useState("");
  const [denialOtherText, setDenialOtherText] = useState("");

  // Shared state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Feedback thread state
  const [feedbackThreads, setFeedbackThreads] = useState([]);
  const [selectedFeedbackThread, setSelectedFeedbackThread] = useState(null);
  const [feedbackFlowStatus, setFeedbackFlowStatus] = useState(null);
  const [feedbackCurrentQuestion, setFeedbackCurrentQuestion] = useState(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showFeedbackTyping, setShowFeedbackTyping] = useState(false);
  const [feedbackSelectedReasons, setFeedbackSelectedReasons] = useState([]);
  const [feedbackOtherText, setFeedbackOtherText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  // PDF viewer modal
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [pdfViewerName, setPdfViewerName] = useState("");

  const messagesEndRef = useRef(null);

  // --- Current user ----------------------------------------------------------

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/auth/current-user/");
        const id = res?.data?.id || res?.data?.user?.id || null;
        setCurrentUserId(id);
      } catch (e) {
        setCurrentUserId(null);
      }
    })();
  }, []);

  // --- Fetch thread lists ----------------------------------------------------

  const fetchConversationThreads = async () => {
    try {
      const response = await axiosInstance.get("/chats/get_users_with_history/");
      setGeneralChats(response.data.activeChats || []);
    } catch (error) {
      console.error("Error fetching conversation threads:", error);
    }

    if (activeTab === "member-portal") {
      try {
        const res = await axiosInstance.get("/member-portal/play-requests/");
        const active = (res.data || []).filter(
          (r) => !["declined", "cancelled"].includes(r.status)
        );
        setMemberPortalChats(active);
      } catch (error) {
        console.error("Error fetching member portal chats:", error);
      }
    }

    if (activeTab === "clubs") {
      try {
        const res = await axiosInstance.get("/messages/club-threads/");
        setClubThreads(res.data.threads || []);
      } catch (error) {
        console.error("Error fetching club threads:", error);
      }
      try {
        const res = await axiosInstance.get("/messages/feedback-threads/");
        setFeedbackThreads(res.data || []);
      } catch (error) {
        console.error("Error fetching feedback threads:", error);
      }
    }
  };

  useEffect(() => {
    fetchConversationThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-select conversation from query params (notification deep-links)
  useEffect(() => {
    const paramUserId = searchParams.get("userId");
    const paramThreadId = searchParams.get("threadId");

    if (activeTab === "general" && paramUserId && generalChats.length > 0) {
      const chat = generalChats.find(c => String(c.id) === paramUserId || String(c.user_id) === paramUserId);
      if (chat) {
        setReceiverId(chat.id || chat.user_id);
        setReceiverUsername(chat.username || chat.name || "User");
        if (isMobile) setMobileShowChat(true);
      } else {
        // User not in chat list — open directly by ID
        setReceiverId(parseInt(paramUserId));
        setReceiverUsername("User");
        if (isMobile) setMobileShowChat(true);
      }
    }

    if (activeTab === "clubs" && paramThreadId && clubThreads.length > 0) {
      const thread = clubThreads.find(t => String(t.interaction_request_id) === paramThreadId || String(t.id) === paramThreadId);
      if (thread) {
        setSelectedClubThread(thread);
        if (isMobile) setMobileShowChat(true);
      }
    }

    const paramFeedbackId = searchParams.get("feedbackId");
    if (activeTab === "clubs" && paramFeedbackId && feedbackThreads.length > 0) {
      const fb = feedbackThreads.find(t => String(t.feedback_id) === paramFeedbackId || String(t.id) === paramFeedbackId);
      if (fb) {
        setSelectedFeedbackThread(fb);
        setSelectedClubThread(null);
        if (isMobile) setMobileShowChat(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalChats, clubThreads, feedbackThreads, activeTab]);

  // --- Fetch messages --------------------------------------------------------

  useEffect(() => {
    if (activeTab === "general" && receiverId) {
      fetchGeneralMessages();
    } else if (activeTab === "member-portal" && selectedPlayRequestId) {
      fetchMemberPortalMessages();
    } else if (activeTab === "clubs" && selectedClubThread) {
      fetchClubThreadMessages(selectedClubThread.interaction_request_id);
    } else if (activeTab === "clubs" && selectedFeedbackThread) {
      fetchFeedbackMessages(selectedFeedbackThread.feedback_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverId, selectedPlayRequestId, selectedClubThread, selectedFeedbackThread, activeTab, currentUserId]);

  const fetchGeneralMessages = async () => {
    if (!receiverId) return;
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/chats/get_messages/${receiverId}/`);
      setMessages(res.data || []);
    } catch (error) {
      console.error("Error fetching general messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeMemberPortalMessage = (msg) => {
    const senderId = msg.sender_id ?? (msg.sender && msg.sender.id) ?? msg.sender ?? null;
    const isMine = idsEqual(senderId, currentUserId);
    const usernameCandidate = msg.sender_name ?? msg.sender_username ?? (isMine ? "You" : "Member");
    return {
      sender: { username: usernameCandidate },
      content: msg.content ?? "",
      timestamp: msg.timestamp ?? msg.created_at ?? new Date().toISOString(),
      is_sent_by_user: !!isMine,
    };
  };

  const fetchMemberPortalMessages = async () => {
    if (!selectedPlayRequestId) return;
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/member-portal/chats/${selectedPlayRequestId}/`);
      const raw = res?.data?.messages || [];
      setMessages(raw.map(normalizeMemberPortalMessage));
    } catch (error) {
      console.error("Error fetching member portal messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClubThreadMessages = async (interactionRequestId) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/messages/club-threads/${interactionRequestId}/`);
      setClubThreadData(res.data);
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching club thread messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFeedbackMessages = async (feedbackId) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/messages/feedback-threads/${feedbackId}/`);
      setMessages(res.data.messages || []);
      setFeedbackFlowStatus(res.data.flow_status);
      setFeedbackCurrentQuestion(res.data.current_question);
    } catch (error) {
      console.error("Error fetching feedback messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Send message ----------------------------------------------------------

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      if (activeTab === "general" && receiverId) {
        await axiosInstance.post("/chats/send_chat/", {
          receiverId,
          content: newMessage,
        });
        setMessages((prev) => [
          ...prev,
          {
            sender: { username: "You" },
            content: newMessage,
            timestamp: new Date().toISOString(),
            is_sent_by_user: true,
          },
        ]);
      } else if (activeTab === "member-portal" && selectedPlayRequestId) {
        await axiosInstance.post(`/member-portal/chats/${selectedPlayRequestId}/send/`, {
          content: newMessage,
        });
        setMessages((prev) => [
          ...prev,
          {
            sender: { username: "You" },
            content: newMessage,
            timestamp: new Date().toISOString(),
            is_sent_by_user: true,
          },
        ]);
      } else if (activeTab === "clubs" && selectedClubThread && clubThreadData?.can_reply) {
        await axiosInstance.post(
          `/messages/club-threads/${selectedClubThread.interaction_request_id}/send/`,
          { content: newMessage }
        );
        setMessages((prev) => [
          ...prev,
          {
            id: null,
            content: newMessage,
            timestamp: new Date().toISOString(),
            is_system_message: false,
            is_sent_by_user: true,
          },
        ]);
      }
      setNewMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  // --- Respond to club request -----------------------------------------------

  const handleRespondToClubRequest = async (decision) => {
    if (!selectedClubThread) return;
    try {
      await axiosInstance.post(
        `/messages/club-threads/${selectedClubThread.interaction_request_id}/respond/`,
        { decision }
      );
      // Refresh messages and thread list
      fetchClubThreadMessages(selectedClubThread.interaction_request_id);
      const res = await axiosInstance.get("/messages/club-threads/");
      setClubThreads(res.data.threads || []);

      // Show denial reason prompt after rejecting
      if (decision === "reject") {
        setDenialRequestId(selectedClubThread.interaction_request_id);
        setSelectedDenialReason("");
        setDenialOtherText("");
        setShowDenialPrompt(true);
      }
    } catch (error) {
      console.error("Error responding to club request:", error);
    }
  };

  // --- Denial reason handlers ------------------------------------------------

  const DENIAL_REASONS = [
    { key: "not_interested", label: "Not Interested" },
    { key: "too_far", label: "Too Far Away" },
    { key: "already_member", label: "Already a Member" },
    { key: "bad_timing", label: "Bad Timing" },
    { key: "other", label: "Other" },
  ];

  const handleSubmitDenialReason = async () => {
    if (!denialRequestId || !selectedDenialReason) return;
    try {
      await axiosInstance.post(
        `/messages/club-threads/${denialRequestId}/denial-reason/`,
        {
          denial_reason: selectedDenialReason,
          denial_reason_text: selectedDenialReason === "other" ? denialOtherText : "",
        }
      );
    } catch (error) {
      // Fire-and-forget — the deny already went through
      console.error("Error submitting denial reason:", error);
    }
    setShowDenialPrompt(false);
    setDenialRequestId(null);
    setSelectedDenialReason("");
    setDenialOtherText("");
  };

  const handleSkipDenialReason = () => {
    setShowDenialPrompt(false);
    setDenialRequestId(null);
    setSelectedDenialReason("");
    setDenialOtherText("");
  };

  // --- Feedback response handlers --------------------------------------------

  const RATING_LABELS = { 1: "Poor", 2: "Below Average", 3: "Average", 4: "Good", 5: "Excellent" };

  const submitFeedbackResponse = async (payload) => {
    if (isSubmittingFeedback || !selectedFeedbackThread) return;
    setIsSubmittingFeedback(true);

    const typingTimer = setTimeout(() => setShowFeedbackTyping(true), 400);

    try {
      const res = await axiosInstance.post(
        `/messages/feedback-threads/${selectedFeedbackThread.feedback_id}/respond/`,
        payload
      );

      await new Promise((resolve) => setTimeout(resolve, 800));

      setShowFeedbackTyping(false);
      setMessages(res.data.messages || []);
      setFeedbackFlowStatus(res.data.flow_status);
      setFeedbackCurrentQuestion(res.data.current_question);

      // Reset input state
      setFeedbackSelectedReasons([]);
      setFeedbackOtherText("");
      setFeedbackRating(0);
      setFeedbackText("");
    } catch (error) {
      console.error("Error submitting feedback response:", error);
      setShowFeedbackTyping(false);
    } finally {
      clearTimeout(typingTimer);
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedbackReasonSelect = () => {
    if (feedbackSelectedReasons.length === 0) return;
    const payload = { reasons: feedbackSelectedReasons };
    if (feedbackSelectedReasons.includes("other") && feedbackOtherText.trim()) {
      payload.reason_text = feedbackOtherText.trim();
    }
    submitFeedbackResponse(payload);
  };

  const handleFeedbackRatingSubmit = () => {
    if (feedbackRating === 0) return;
    submitFeedbackResponse({ rating: feedbackRating });
  };

  const handleFeedbackTextSubmit = () => {
    submitFeedbackResponse({ feedback: feedbackText.trim() });
  };

  const handleFeedbackTextSkip = () => {
    submitFeedbackResponse({ feedback: "" });
  };

  // --- Auto-scroll -----------------------------------------------------------

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showFeedbackTyping]);

  // --- Tab switching ---------------------------------------------------------

  const switchTab = (tab) => {
    setActiveTab(tab);
    setMessages([]);
    setReceiverId(null);
    setReceiverUsername("");
    setSelectedPlayRequestId(null);
    setSelectedClubThread(null);
    setClubThreadData(null);
    setSelectedFeedbackThread(null);
    setFeedbackFlowStatus(null);
    setFeedbackCurrentQuestion(null);
    setFeedbackSelectedReasons([]);
    setFeedbackOtherText("");
    setFeedbackRating(0);
    setFeedbackText("");
    setMobileShowChat(false);
    setSearchQuery("");
  };

  // --- Selection handlers ----------------------------------------------------

  const clearFeedbackInputState = () => {
    setSelectedFeedbackThread(null);
    setFeedbackFlowStatus(null);
    setFeedbackCurrentQuestion(null);
    setFeedbackSelectedReasons([]);
    setFeedbackOtherText("");
    setFeedbackRating(0);
    setFeedbackText("");
  };

  const handleGeneralUserSelect = (user) => {
    setReceiverUsername(user.username);
    setReceiverId(user.id);
    setSelectedPlayRequestId(null);
    setSelectedClubThread(null);
    clearFeedbackInputState();
    setSearchQuery("");
    if (isMobile) setMobileShowChat(true);
  };

  const handleMemberPortalChatSelect = (request) => {
    const otherUser = request.host?.user || request.requester?.user;
    const otherUserName = otherUser?.first_name || otherUser?.username || "Member";
    setReceiverUsername(`${otherUserName} — ${request.target_club?.name}`);
    setReceiverId(otherUser?.id || null);
    setSelectedPlayRequestId(request.request_id);
    setSelectedClubThread(null);
    clearFeedbackInputState();
    setSearchQuery("");
    if (isMobile) setMobileShowChat(true);
  };

  const handleClubThreadSelect = (thread) => {
    setSelectedClubThread(thread);
    setReceiverUsername(thread.club.name);
    setReceiverId(null);
    setSelectedPlayRequestId(null);
    clearFeedbackInputState();
    setSearchQuery("");
    if (isMobile) setMobileShowChat(true);
  };

  const handleFeedbackThreadSelect = (thread) => {
    setSelectedFeedbackThread(thread);
    setReceiverId(null);
    setSelectedPlayRequestId(null);
    setSelectedClubThread(null);
    setClubThreadData(null);
    setFeedbackSelectedReasons([]);
    setFeedbackOtherText("");
    setFeedbackRating(0);
    setFeedbackText("");
    setSearchQuery("");
    if (isMobile) setMobileShowChat(true);
  };

  // --- Derived state ---------------------------------------------------------

  const getDisplayName = (m) => {
    if (!m || m.sender == null) return "User";
    return typeof m.sender === "string"
      ? m.sender
      : m.sender.username || m.sender.name || "User";
  };

  const hasActiveConversation =
    (activeTab === "general" && receiverId) ||
    (activeTab === "member-portal" && selectedPlayRequestId) ||
    (activeTab === "clubs" && (selectedClubThread || selectedFeedbackThread));

  const canSendMessage =
    (activeTab === "general" && receiverId) ||
    (activeTab === "member-portal" && selectedPlayRequestId) ||
    (activeTab === "clubs" && clubThreadData?.can_reply);

  const isFeedbackConversation = activeTab === "clubs" && selectedFeedbackThread;

  const getConversationTitle = () => {
    if (isFeedbackConversation) return "PrivateIQ";
    if (activeTab === "clubs" && selectedClubThread) return selectedClubThread.club.name;
    return receiverUsername || "Chat";
  };

  const getConversationSubtitle = () => {
    if (isFeedbackConversation) {
      return `About ${selectedFeedbackThread.club_name}`;
    }
    if (activeTab === "clubs" && selectedClubThread) {
      const c = selectedClubThread.club;
      return c.city && c.state ? `${c.city}, ${c.state}` : "";
    }
    if (activeTab === "member-portal" && selectedPlayRequestId) return "Member Portal";
    return "";
  };

  const getAvatarInitial = () => {
    if (isFeedbackConversation) return "P";
    const title = getConversationTitle();
    return title ? title.charAt(0) : "?";
  };

  // --- Filtering threads by search -------------------------------------------

  const searchLower = searchQuery.toLowerCase();

  const filteredGeneralChats = (generalChats || []).filter((chat) =>
    (chat.username || "").toLowerCase().includes(searchLower)
  );

  const filteredMemberPortalChats = (memberPortalChats || []).filter((r) => {
    const clubName = r.target_club?.name || "";
    const otherUser = r.host?.user || r.requester?.user;
    const userName = otherUser?.first_name || otherUser?.username || "";
    return userName.toLowerCase().includes(searchLower) || clubName.toLowerCase().includes(searchLower);
  });

  const filteredClubThreads = (clubThreads || [])
    .filter((t) => (t.club?.name || "").toLowerCase().includes(searchLower))
    .sort((a, b) => {
      const ta = a.latest_message?.timestamp || a.created_at || "";
      const tb = b.latest_message?.timestamp || b.created_at || "";
      return new Date(tb) - new Date(ta);
    });

  const filteredFeedbackThreads = (feedbackThreads || [])
    .filter((t) => (t.club_name || "").toLowerCase().includes(searchLower));

  // --- Render: Thread List ---------------------------------------------------

  const renderThreadList = () => {
    if (activeTab === "general") {
      if (filteredGeneralChats.length === 0) {
        return <div className="chat-empty-threads"><p>No conversations yet.</p></div>;
      }
      return filteredGeneralChats.map((user) => (
        <div
          key={user.id}
          className={`chat-thread-item ${idsEqual(receiverId, user.id) ? "chat-thread-item--active" : ""}`}
          onClick={() => handleGeneralUserSelect(user)}
        >
          <div className="chat-thread-avatar chat-thread-avatar--user">
            {(user.username || "?").charAt(0)}
          </div>
          <div className="chat-thread-content">
            <div className="chat-thread-header">
              <span className="chat-thread-name">{user.username}</span>
            </div>
          </div>
        </div>
      ));
    }

    if (activeTab === "member-portal") {
      if (filteredMemberPortalChats.length === 0) {
        return <div className="chat-empty-threads"><p>No member portal conversations yet.</p></div>;
      }
      return filteredMemberPortalChats.map((request) => {
        const isCurrentUserHost = currentUserId && idsEqual(request.host?.user?.id, currentUserId);
        const otherUser = isCurrentUserHost ? request.requester?.user : request.host?.user;
        const otherUserName = otherUser?.first_name || otherUser?.username || "Member";
        const isActive = selectedPlayRequestId === request.request_id;

        return (
          <div
            key={request.request_id}
            className={`chat-thread-item ${isActive ? "chat-thread-item--active" : ""}`}
            onClick={() => handleMemberPortalChatSelect(request)}
          >
            <div className="chat-thread-avatar chat-thread-avatar--portal">
              {otherUserName.charAt(0)}
            </div>
            <div className="chat-thread-content">
              <div className="chat-thread-header">
                <span className="chat-thread-name">{otherUserName}</span>
                {request.scheduled_date && (
                  <span className="chat-thread-time">
                    {new Date(request.scheduled_date).toLocaleDateString([], { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
              <div className="chat-thread-subtitle">{request.target_club?.name}</div>
              <span className={`chat-thread-status chat-thread-status--${request.status}`}>
                {request.status}
              </span>
            </div>
          </div>
        );
      });
    }

    if (activeTab === "clubs") {
      // Merge club threads and feedback threads, sorted by time
      const clubItems = filteredClubThreads.map((t) => ({
        type: "club",
        data: t,
        time: t.latest_message?.timestamp || t.created_at || "",
      }));
      const feedbackItems = filteredFeedbackThreads.map((t) => ({
        type: "feedback",
        data: t,
        time: t.last_message_time || "",
      }));
      const allItems = [...clubItems, ...feedbackItems].sort(
        (a, b) => new Date(b.time) - new Date(a.time)
      );

      if (allItems.length === 0) {
        return <div className="chat-empty-threads"><p>No club messages yet. When clubs reach out, their messages will appear here.</p></div>;
      }

      return allItems.map((item) => {
        if (item.type === "club") {
          const thread = item.data;
          const isActive = selectedClubThread?.interaction_request_id === thread.interaction_request_id;
          const hasUnread = thread.unread_count > 0;

          return (
            <div
              key={`club-${thread.interaction_request_id}`}
              className={`chat-thread-item ${isActive ? "chat-thread-item--active" : ""} ${hasUnread ? "chat-thread-item--unread" : ""}`}
              onClick={() => handleClubThreadSelect(thread)}
            >
              <div className="chat-thread-avatar chat-thread-avatar--club">
                <span className="chat-club-acronym">{getClubAcronym(thread.club.name)}</span>
              </div>
              <div className="chat-thread-content">
                <div className="chat-thread-header">
                  <span className="chat-thread-name">{thread.club.name}</span>
                  <span className="chat-thread-time">
                    {formatConversationTime(thread.latest_message?.timestamp)}
                  </span>
                </div>
                <div className="chat-thread-preview">
                  <span className="chat-thread-last-message">
                    {thread.latest_message?.content || "New connection"}
                  </span>
                  {hasUnread && <span className="chat-thread-badge">{thread.unread_count}</span>}
                </div>
                {thread.club.city && (
                  <div className="chat-thread-subtitle">{thread.club.city}, {thread.club.state}</div>
                )}
                {thread.status === "pending" && (
                  <span className="chat-thread-status chat-thread-status--pending">Pending</span>
                )}
              </div>
            </div>
          );
        }

        // Feedback thread
        const fb = item.data;
        const isActive = selectedFeedbackThread?.feedback_id === fb.feedback_id;
        const hasUnread = fb.unread_count > 0;
        const isCompleted = fb.flow_status === "completed";

        return (
          <div
            key={`fb-${fb.feedback_id}`}
            className={`chat-thread-item ${isActive ? "chat-thread-item--active" : ""} ${hasUnread ? "chat-thread-item--unread" : ""}`}
            onClick={() => handleFeedbackThreadSelect(fb)}
          >
            <div className="chat-thread-avatar chat-thread-avatar--piq">
              <img src={piqLogo} alt="PIQ" className="chat-piq-logo" />
            </div>
            <div className="chat-thread-content">
              <div className="chat-thread-header">
                <span className="chat-thread-name">PrivateIQ</span>
                <span className="chat-thread-time">
                  {formatConversationTime(fb.last_message_time)}
                </span>
              </div>
              <div className="chat-thread-preview">
                <span className="chat-thread-last-message">
                  {fb.last_message || "Feedback request"}
                </span>
                {hasUnread && !isCompleted && (
                  <span className="chat-feedback-badge">FEEDBACK</span>
                )}
                {isCompleted && (
                  <span className="chat-feedback-completed">Completed</span>
                )}
                {hasUnread && !isCompleted && fb.unread_count > 0 && (
                  <span className="chat-thread-badge">{fb.unread_count}</span>
                )}
              </div>
              <div className="chat-thread-subtitle">
                About {fb.club_name}{fb.club_city ? `, ${fb.club_city}` : ""}{fb.club_state ? `, ${fb.club_state}` : ""}
              </div>
            </div>
          </div>
        );
      });
    }

    return null;
  };

  // --- Render: Messages ------------------------------------------------------

  const renderMessages = () => {
    if (isLoading) {
      return (
        <div className="chat-loading">
          <div className="chat-spinner" />
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="chat-empty-threads">
          <p>No messages yet. Start the conversation!</p>
        </div>
      );
    }

    return messages.map((msg, index) => {
      const prevMsg = index > 0 ? messages[index - 1] : null;
      const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
      const showDate = shouldShowDateSeparator(msg, prevMsg);
      const grouped = isGroupedMessage(msg, prevMsg);
      const isLastInGroup = !nextMsg || !isGroupedMessage(nextMsg, msg) || (nextMsg && nextMsg.is_system_message);

      // System message
      if (msg.is_system_message) {
        const statusClass = msg.status_type ? ` chat-system-message--${msg.status_type}` : "";
        return (
          <React.Fragment key={msg.id || `sys-${index}`}>
            {showDate && (
              <div className="chat-date-separator">
                <span>{formatDateSeparator(msg.timestamp)}</span>
              </div>
            )}
            <div className={`chat-system-message${statusClass}`}>
              <span>{msg.content}</span>
              {msg.actions && msg.actions.includes("approve") && (
                <div className="chat-system-actions">
                  <button className="chat-action-approve" onClick={() => handleRespondToClubRequest("approve")}>
                    Approve
                  </button>
                  <button className="chat-action-reject" onClick={() => handleRespondToClubRequest("reject")}>
                    Decline
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      }

      // Regular message
      const isSent = msg.is_sent_by_user;
      const senderName = isFeedbackConversation && !isSent
        ? (msg.sender_name || "PrivateIQ")
        : activeTab === "clubs" && !isSent
        ? msg.sender_name
        : (!isSent ? getDisplayName(msg) : null);

      return (
        <React.Fragment key={msg.id || index}>
          {showDate && (
            <div className="chat-date-separator">
              <span>{formatDateSeparator(msg.timestamp)}</span>
            </div>
          )}
          <div
            className={`chat-msg ${isSent ? "chat-msg--sent" : "chat-msg--received"} ${grouped ? "chat-msg--grouped" : ""} ${isLastInGroup ? "chat-msg--last" : ""}`}
          >
            {!grouped && senderName && (
              <div className="chat-msg-sender">{senderName}</div>
            )}
            <div className="chat-msg-bubble">
              {msg.content && <p className="chat-msg-text">{msg.content}</p>}
              {msg.attachment && (
                <button
                  className="chat-msg-attachment"
                  onClick={() => {
                    setPdfViewerUrl(msg.attachment.url);
                    setPdfViewerName(msg.attachment.name);
                  }}
                >
                  <Icons.File />
                  <span className="chat-msg-attachment-info">
                    <span className="chat-msg-attachment-name">{msg.attachment.name}</span>
                    <span className="chat-msg-attachment-size">{formatFileSize(msg.attachment.size)}</span>
                  </span>
                </button>
              )}
            </div>
            {isLastInGroup && (
              <span className="chat-msg-meta">{formatMessageTime(msg.timestamp)}</span>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  // --- Render ----------------------------------------------------------------

  return (
    <div className="chat-component">
      {/* Tab bar */}
      <div className="chat-tab-container">
        <button
          className={`chat-tab-button ${activeTab === "general" ? "chat-tab-active" : ""}`}
          onClick={() => switchTab("general")}
        >
          General
        </button>
        <button
          className={`chat-tab-button ${activeTab === "member-portal" ? "chat-tab-active" : ""}`}
          onClick={() => switchTab("member-portal")}
        >
          Member Portal
        </button>
        <button
          className={`chat-tab-button ${activeTab === "clubs" ? "chat-tab-active" : ""}`}
          onClick={() => switchTab("clubs")}
        >
          Clubs
        </button>
      </div>

      {/* Two-panel layout */}
      <div className="chat-panels">
        {/* Left panel: Thread list */}
        <div className={`chat-thread-list ${isMobile && mobileShowChat ? "chat-thread-list--hidden" : ""}`}>
          <div className="chat-search-container">
            <input
              type="text"
              className="chat-search-input"
              placeholder={
                activeTab === "general" ? "Search users..." :
                activeTab === "member-portal" ? "Search conversations..." :
                "Search clubs..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {renderThreadList()}
          </div>
        </div>

        {/* Right panel: Conversation */}
        {(!isMobile || mobileShowChat) && hasActiveConversation ? (
          <div className="chat-conversation">
            {/* Conversation header */}
            <div className="chat-conv-header">
              {isMobile && (
                <button className="chat-back-btn" onClick={() => setMobileShowChat(false)}>
                  <Icons.ChevronLeft />
                </button>
              )}
              <div className={`chat-conv-avatar${isFeedbackConversation ? " chat-conv-avatar--piq" : activeTab === "clubs" && selectedClubThread ? " chat-conv-avatar--club" : ""}`}>
                {isFeedbackConversation
                  ? <img src={piqLogo} alt="PIQ" className="chat-piq-logo" />
                  : activeTab === "clubs" && selectedClubThread
                  ? <span className="chat-club-acronym">{getClubAcronym(selectedClubThread.club.name)}</span>
                  : getAvatarInitial()
                }
              </div>
              <div className="chat-conv-info">
                {activeTab === "clubs" && selectedClubThread?.club ? (
                  <h4
                    className="chat-conv-club-link"
                    onClick={() => navigate(`/club/${selectedClubThread.club.slug || selectedClubThread.club.club_id}`)}
                  >
                    {getConversationTitle()}
                  </h4>
                ) : (
                  <h4>{getConversationTitle()}</h4>
                )}
                {getConversationSubtitle() && (
                  <span className="chat-conv-meta">{getConversationSubtitle()}</span>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages-area">
              {renderMessages()}
              {/* Typing indicator for feedback */}
              {showFeedbackTyping && (
                <div className="chat-typing-indicator">
                  <div className="chat-typing-bubble">
                    <span className="chat-typing-dot" />
                    <span className="chat-typing-dot" />
                    <span className="chat-typing-dot" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area — feedback, regular, or disabled */}
            {isFeedbackConversation ? (
              <div className="chat-feedback-input">
                {showFeedbackTyping ? null
                : feedbackFlowStatus === "completed" ? (
                  <div className="chat-feedback-completed-footer">Feedback submitted</div>
                ) : feedbackFlowStatus === "expired" ? null
                : feedbackCurrentQuestion?.type === "multi_select" ? (
                  <div className="chat-feedback-multi">
                    <div className="chat-feedback-hint">Select all that apply</div>
                    <div className="chat-feedback-chips">
                      {(feedbackCurrentQuestion.options || []).map((opt) => (
                        <button
                          key={opt.value}
                          className={`chat-feedback-chip ${feedbackSelectedReasons.includes(opt.value) ? "chat-feedback-chip--selected" : ""}`}
                          onClick={() => {
                            setFeedbackSelectedReasons((prev) =>
                              prev.includes(opt.value)
                                ? prev.filter((v) => v !== opt.value)
                                : [...prev, opt.value]
                            );
                          }}
                          disabled={isSubmittingFeedback}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {feedbackSelectedReasons.includes("other") && feedbackCurrentQuestion.allow_other_text && (
                      <input
                        type="text"
                        className="chat-feedback-other-input"
                        placeholder="Please specify..."
                        value={feedbackOtherText}
                        onChange={(e) => setFeedbackOtherText(e.target.value)}
                        maxLength={500}
                        disabled={isSubmittingFeedback}
                      />
                    )}
                    {feedbackSelectedReasons.length > 0 && (
                      <div className="chat-feedback-submit-row">
                        <button
                          className="chat-feedback-submit-btn"
                          onClick={handleFeedbackReasonSelect}
                          disabled={isSubmittingFeedback || (feedbackSelectedReasons.includes("other") && !feedbackOtherText.trim())}
                        >
                          Submit
                        </button>
                      </div>
                    )}
                  </div>
                ) : feedbackCurrentQuestion?.type === "rating" ? (
                  <div className="chat-feedback-rating">
                    <div className="chat-feedback-hint">Tap to rate (1-5)</div>
                    <div className="chat-feedback-bars">
                      {[1, 2, 3, 4, 5].map((val) => (
                        <button
                          key={val}
                          className={`chat-feedback-bar ${feedbackRating >= val ? "chat-feedback-bar--active" : ""}`}
                          onClick={() => setFeedbackRating(val)}
                          disabled={isSubmittingFeedback}
                        />
                      ))}
                    </div>
                    {feedbackRating > 0 && (
                      <div className="chat-feedback-rating-label">{RATING_LABELS[feedbackRating]}</div>
                    )}
                    <button
                      className={`chat-feedback-rating-submit ${feedbackRating === 0 ? "chat-feedback-rating-submit--disabled" : ""}`}
                      onClick={handleFeedbackRatingSubmit}
                      disabled={feedbackRating === 0 || isSubmittingFeedback}
                    >
                      Submit Rating
                    </button>
                  </div>
                ) : feedbackCurrentQuestion?.type === "text_input" ? (
                  <div className="chat-feedback-text">
                    <textarea
                      className="chat-feedback-text-area"
                      placeholder="Share any additional thoughts..."
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      maxLength={1000}
                      rows={3}
                      disabled={isSubmittingFeedback}
                    />
                    <div className="chat-feedback-btn-row">
                      <button
                        className="chat-feedback-skip-btn"
                        onClick={handleFeedbackTextSkip}
                        disabled={isSubmittingFeedback}
                      >
                        Skip
                      </button>
                      <button
                        className="chat-feedback-submit-btn"
                        onClick={handleFeedbackTextSubmit}
                        disabled={!feedbackText.trim() || isSubmittingFeedback}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : canSendMessage ? (
              <form className="chat-input-area" onSubmit={handleSendMessage}>
                <div className="chat-input-wrapper">
                  <input
                    type="text"
                    className="chat-input-field"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="chat-send-btn"
                  disabled={!newMessage.trim()}
                >
                  <Icons.Send />
                </button>
              </form>
            ) : activeTab === "clubs" && clubThreadData ? (
              <div className="chat-reply-disabled">
                {clubThreadData.interaction_request?.status === "pending"
                  ? "Approve this connection to start messaging"
                  : "This connection was declined"}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="chat-conversation chat-conversation--empty">
            <div className="chat-no-selection">
              <div className="chat-no-selection-icon">
                <Icons.Message />
              </div>
              <h3>Select a conversation</h3>
              <p>
                {activeTab === "general"
                  ? "Choose someone from the list to start messaging"
                  : activeTab === "member-portal"
                  ? "Select a member portal conversation"
                  : "Select a club to view messages"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {pdfViewerUrl && (
        <div className="chat-pdf-overlay" onClick={() => { setPdfViewerUrl(null); setPdfViewerName(""); }}>
          <div className="chat-pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-pdf-header">
              <h3>{pdfViewerName || "PDF"}</h3>
              <button
                type="button"
                className="chat-pdf-close"
                onClick={() => { setPdfViewerUrl(null); setPdfViewerName(""); }}
              >
                <Icons.Close />
              </button>
            </div>
            <div className="chat-pdf-body">
              <iframe
                src={pdfViewerUrl}
                title={pdfViewerName || "PDF"}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>
      )}

      {/* Denial Reason Modal */}
      {showDenialPrompt && (
        <div className="denial-reason-overlay" onClick={handleSkipDenialReason}>
          <div className="denial-reason-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="denial-reason-title">Mind sharing why?</h3>
            <p className="denial-reason-subtitle">Your feedback helps clubs improve their outreach</p>
            <div className="denial-reason-chips">
              {DENIAL_REASONS.map((reason) => (
                <button
                  key={reason.key}
                  className={`denial-reason-chip ${selectedDenialReason === reason.key ? "denial-reason-chip--selected" : ""}`}
                  onClick={() => {
                    setSelectedDenialReason(reason.key);
                    if (reason.key !== "other") setDenialOtherText("");
                  }}
                >
                  {reason.label}
                </button>
              ))}
            </div>
            {selectedDenialReason === "other" && (
              <textarea
                className="denial-reason-other-input"
                placeholder="Tell us more..."
                value={denialOtherText}
                onChange={(e) => setDenialOtherText(e.target.value)}
                rows={3}
                maxLength={500}
              />
            )}
            <div className="denial-reason-actions">
              <button
                className="denial-reason-submit"
                disabled={!selectedDenialReason || (selectedDenialReason === "other" && !denialOtherText.trim())}
                onClick={handleSubmitDenialReason}
              >
                Submit
              </button>
              <button className="denial-reason-skip" onClick={handleSkipDenialReason}>
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
